// Calculo de custo do Monitor de Custos — Fornecedores.
// REGRA DE DINHEIRO (replica exata do mockup-monitor-martins.html, confira contra ele):
//   - cupom: fracao global da semana (default 0.07).
//   - DIFAL: 0.0449, aplicado SO quando UF=MG. ORDEM: cupom PRIMEIRO, DIFAL DEPOIS.
//   - markup: fracao FIXA por familia.
//   - preco por unidade = preco_embalagem / qtd  (metros p/ cabos, pecas p/ conectores etc.)
//   - custo_unit = preco_unit * (1 - cupom); se MG: custo_unit *= (1 + DIFAL)
//   - FC Pix (por unidade) = custo_unit * (1 + markup)
//   - Nosso Pix por unidade = preco_pix do produto / qtd da unidade de venda.
//     (preco_pix e o preco da UNIDADE INTEIRA, NAO por metro/peca — ver nossoPixPorUnidade)
//   - Variacao = (NossoPixUnit / FC Pix) - 1  (fracao; *100 = %)
//     = (Praticado / Sugestao de Venda) - 1. Positivo => praticado ACIMA do sugerido
//     (margem folgada, da pra baixar); negativo => praticado ABAIXO (custo subiu, suba o preco).

export const DIFAL_MG = 0.0449;

// As aliquotas de DIFAL que a oferta pode escolher ([DA-727]), em %. Saem da config fiscal da casa
// (interna efetiva 11%): nacional, interestadual 7% -> (0,11-0,07)/0,89 = 4,49%; importado, 4% ->
// (0,11-0,04)/0,89 = 7,87%. Medido nas notas de 120 dias: 4,49 e 7,87 sao as duas que aparecem.
// Lista UNICA: a caixa da tela e a validacao da rota leem daqui.
export const ALIQUOTAS_DIFAL = [4.49, 7.87] as const;
export function aliquotaDifalValida(pct: number | null | undefined): boolean {
  return pct != null && ALIQUOTAS_DIFAL.some((a) => Math.abs(a - Number(pct)) < 0.001);
}

export interface OfertaCalcEntrada {
  precoEmbalagem: number | null; // null/indisponivel -> ND
  qtd: number;                   // quantidade na embalagem (metros p/ cabos, pecas p/ outros; >0)
  uf: string;                    // 'GO' | 'MG' | ...
  // aliquota escolhida na oferta, em % (4.49 / 7.87). null/ausente = a conferir -> usa DIFAL_MG.
  difalPct?: number | null;
  // Fracao do preco lido que vira CUSTO, pra fornecedor cujo robo le o preco de VAREJO do site
  // (Tramontina, [DA-804]: o representante cobra ~46,28% do a vista). null/ausente = 1 (o preco
  // lido JA e o custo, como na Martins e na Tambasa). Sai de fatorDaOferta.
  fator?: number | null;
}

// custo por unidade de uma oferta (com cupom; e DIFAL se MG; e o fator do site). null se indisponivel.
export function custoUnit(of: OfertaCalcEntrada, cupom: number): number | null {
  if (of.precoEmbalagem == null || !(of.qtd > 0)) return null;
  const precoUnit = of.precoEmbalagem / of.qtd;
  let custo = precoUnit * (1 - cupom);
  if ((of.uf || '').toUpperCase() === 'MG') custo = custo * (1 + fracaoDifal(of.difalPct));
  if (of.fator != null && of.fator > 0) custo = custo * of.fator;
  return custo;
}

// FATOR DO SITE ([DA-804]), em %: o da LINHA manda (calibrado por ele contra uma proposta real);
// sem ele, o do fornecedor. Fora da faixa (0, 100] nao vale — cai no seguinte. Devolve FRACAO.
export const FATOR_SITE_PADRAO_PCT = 46.28;
export function fatorSiteValido(pct: number | null | undefined): boolean {
  return pct != null && Number.isFinite(Number(pct)) && Number(pct) > 0 && Number(pct) <= 100;
}
// Fator a partir do PRECO DO REPRESENTANTE ([DA-804] p.7, emenda de 09/10/2026 — decisao dele): ele
// digita o preco da EMBALAGEM da proposta (a caixa de `qtd` pecas, como o PR FORNEC) e o site vende
// a PECA. Entao fator = (representante / qtd) / site. Devolve o fator em % com 3 casas, ou null
// quando a conta nao fecha (sem site, ou fora de (0, 100]).
// Ex. real: 1P 10A, caixa de 12 a 62,14 contra a peca a 12,55 no site = 5,1783 / 12,55 = 41,26%.
export function fatorDoRepresentante(precoRep: number | null | undefined, precoSite: number | null | undefined, qtd: number | null | undefined): number | null {
  if (precoRep == null || precoSite == null || !(precoRep > 0) || !(precoSite > 0)) return null;
  const q = qtd != null && qtd > 0 ? qtd : 1;
  const pct = Math.round((precoRep / q / precoSite) * 100 * 1000) / 1000;
  return fatorSiteValido(pct) ? pct : null;
}

// De onde sai o custo de uma linha de VAREJO ([DA-804] p.7, emendas de 09/10/2026). Tres casos:
//  - o normal: o preco do site e de UMA PECA — o custo e site x fator, SEM dividir pela qtd (a qtd
//    da linha e a caixa do representante, nao a do site);
//  - produto que o representante vende e o site NUNCA leu (o 1P 6A, 58011103): nao ha fator a
//    calcular — o custo E o preco do representante, por unidade (embalagem / qtd);
//  - preco do representante que nao fecha com um site LIDO: segue site x fator do topo, e a linha
//    avisa (`repNaoFecha`) — nunca vira custo um numero que a conta acabou de recusar.
// `fator` null = linha que nao e de varejo: nada muda.
export function baseDaOfertaDeSite(o: {
  precoEmbalagem: number | null; qtd: number; fator: number | null;
  precoRepresentante: number | null | undefined; precoSiteReferencia: number | null | undefined;
  siteJaLido: boolean;
}): { precoEmbalagem: number | null; qtd: number; fator: number | null; doRepresentante: boolean; repNaoFecha: boolean } {
  if (o.fator == null) return { precoEmbalagem: o.precoEmbalagem, qtd: o.qtd, fator: null, doRepresentante: false, repNaoFecha: false };
  const repPendente = o.precoRepresentante != null && o.precoRepresentante > 0 && o.precoSiteReferencia == null;
  if (repPendente && !o.siteJaLido && o.qtd > 0) {
    return { precoEmbalagem: o.precoRepresentante!, qtd: o.qtd, fator: null, doRepresentante: true, repNaoFecha: false };
  }
  return { precoEmbalagem: o.precoEmbalagem, qtd: 1, fator: o.fator, doRepresentante: false, repNaoFecha: repPendente && o.siteJaLido };
}

// Preco da EMBALAGEM que a linha de varejo leva pro Catalogo do Fornecedor ([DA-804] p.9, decisao dele
// em 09/10/2026: "Custo estimado"). E o custo unitario da TELA (mesma baseDaOfertaDeSite + custoUnit)
// vezes a qtd da caixa, com 4 casas como a tela (centavo cortaria o 12,3144 do 58011103) — o numero que o PR FORNEC da proposta teria. Sem cupom (desconto
// da Martins, [DA-750]) e sem DIFAL (a linha e sempre GO; o Catalogo guarda preco sem DIFAL, [DA-727]).
// null = a tela tambem nao tem custo (ND): o Catalogo fica sem preco, como nos outros robos ([DA-726]).
export function precoEmbalagemDeVarejo(o: Parameters<typeof baseDaOfertaDeSite>[0]): number | null {
  if (o.fator == null || !(o.qtd > 0)) return null;
  const base = baseDaOfertaDeSite(o);
  const unit = custoUnit({ precoEmbalagem: base.precoEmbalagem, qtd: base.qtd, uf: UF_CASA_DIFAL, fator: base.fator }, 0);
  return unit == null ? null : Math.round(unit * o.qtd * 10000) / 10000;
}

export function fatorDaOferta(fatorLinhaPct: number | null | undefined, fatorFornecedorPct: number | null | undefined): number {
  if (fatorSiteValido(fatorLinhaPct)) return Number(fatorLinhaPct) / 100;
  if (fatorSiteValido(fatorFornecedorPct)) return Number(fatorFornecedorPct) / 100;
  return FATOR_SITE_PADRAO_PCT / 100;
}

// A fracao aplicada: a escolhida na oferta, ou a estimativa de 4,49% enquanto ela esta a conferir.
export function fracaoDifal(difalPct: number | null | undefined): number {
  return aliquotaDifalValida(difalPct) ? Number(difalPct) / 100 : DIFAL_MG;
}

// PRECO DA EMBALAGEM COM DIFAL na cotacao ([DA-727]). Decisao dele: "Na cotacao deve mostrar o preco
// da emb com difal e o preco unitario fracionado do preco da emb" — e "o preco que eu edito e o preco
// da Martins, e o sistema calcula o preco com Difal". Por isso NADA grava este numero: cotacao e
// catalogo guardam o preco do fornecedor, e a conta mora aqui.
// Filial de GO (a casa) nao tem DIFAL. UF DESCONHECIDA tambem nao soma: 36 empresas tem a filial
// principal sem UF no cadastro (medido 12/09/2026), e somar ali seria afirmar um imposto sem saber.
// Fora de GO sem aliquota escolhida vale a estimativa de 4,49% — medido nas notas de 120 dias, e a
// taxa de MG, SP, SC e PR pra produto nacional.
export const UF_CASA_DIFAL = 'GO';
export function difalSeAplica(uf: string | null | undefined): boolean {
  const u = String(uf || '').trim().toUpperCase();
  return u !== '' && u !== UF_CASA_DIFAL;
}
export function precoEmbComDifal(preco: number, uf: string | null | undefined, difalPct: number | null | undefined): number {
  if (!Number.isFinite(preco)) return 0;
  return difalSeAplica(uf) ? preco * (1 + fracaoDifal(difalPct)) : preco;
}

// FC Pix por unidade = custo_unit * (1 + markup).
export function fcPixUnit(custo: number, markup: number): number {
  return custo * (1 + markup);
}

// De onde sai o preco da embalagem de uma oferta (migration 0289).
//
// A COLETA manda sempre que consegue ler; o DIGITADO so cobre o buraco — e o contrario da descricao
// ([DA-603]), de proposito: descricao e cadastro e nao envelhece, preco e medicao. Medido em
// 12/09/2026: das 32 ofertas que ja bateram em 'codigo_nao_encontrado', 7 voltaram a ter preco
// sozinhas; com o digitado mandando, essas 7 ficariam congeladas num numero velho — e esse numero
// vira preco de venda pelo botao "Atualizar N produtos".
//
// ⚠️ Isto e peca UNICA porque a tela e a previa de precos precisam responder igual: a previa promete
// devolver o mesmo numero que a gravacao vai usar, e duas copias da regra divergem no dia em que uma
// delas mudar. Quem repete a leitura repete o SQL, nunca a decisao.
export interface PrecoDaOfertaEntrada {
  precoColetado: number | null;   // da coleta corrente; null = ND, motivo, ou nunca coletada
  precoDigitado: number | null;   // monitor_oferta.preco_embalagem_manual
}
export interface PrecoDaOferta {
  preco: number | null;
  digitado: boolean;              // true = a linha esta exibindo o valor digitado
}
export function precoDaOferta(e: PrecoDaOfertaEntrada): PrecoDaOferta {
  if (e.precoColetado != null) return { preco: e.precoColetado, digitado: false };
  if (e.precoDigitado != null) return { preco: e.precoDigitado, digitado: true };
  return { preco: null, digitado: false };
}

// Que preco cada ARMAZEM recebe no Catalogo do Fornecedor depois de uma coleta ([DA-726]).
//
// Ate 12/09/2026 o espelho so escrevia a UF que VEIO com preco; a que nao veio ficava com o valor
// antigo pra sempre. O 4200267 MG passou 36 dias exibindo 195,12 na cotacao, porque as coletas de
// 29/08 e 02/09 so trouxeram GO. Decisao dele: "Pode zerar quando o Monitor nao encontrar" — a
// distorcao (o site sem aquele armazem num dia) ele corrige pelo preco digitado da oferta.
//
// Devolve so as UFs COM preco; toda perna do item fora dessa lista fica SEM preco. O digitado entra
// com a MESMA precedencia da tela (`precoDaOferta`): so quando a coleta nao trouxe preco nenhum, e na
// UF da oferta — senao o Monitor mostraria um numero e o Catalogo guardaria outro.
// ⚠️ `nao_medido` nao passa por aqui: o coletor nao chegou a olhar a pagina, a rota nem grava coleta
// e o Monitor segue exibindo o preco anterior — o Catalogo acompanha o que o Monitor mostra.
export interface PrecosDaColetaEntrada {
  candidatos?: { uf: string; preco_embalagem?: number | null; disponivel?: boolean }[] | null;
  precoVencedor: number | null;   // preco que a coleta gravou (null = ND)
  ufDaOferta: string | null;      // UF final da oferta depois da coleta
  motivo: string | null;          // 'codigo_nao_encontrado' = o site devolveu outro produto
  precoDigitado: number | null;   // monitor_oferta.preco_embalagem_manual
}
export function precosDaColetaPorUf(e: PrecosDaColetaEntrada): Map<string, number> {
  const out = new Map<string, number>();
  const uf = (s: string | null | undefined) => String(s || '').trim().toUpperCase();
  if (!e.motivo) {
    if (e.candidatos && e.candidatos.length) {
      for (const c of e.candidatos) {
        if (c.disponivel && c.preco_embalagem != null && Number(c.preco_embalagem) > 0 && uf(c.uf)) {
          out.set(uf(c.uf), Number(c.preco_embalagem));
        }
      }
    } else if (e.precoVencedor != null && e.precoVencedor > 0 && uf(e.ufDaOferta)) {
      out.set(uf(e.ufDaOferta), e.precoVencedor);
    }
  }
  if (out.size === 0 && uf(e.ufDaOferta)) {
    const { preco, digitado } = precoDaOferta({ precoColetado: null, precoDigitado: e.precoDigitado });
    if (digitado && preco != null && preco > 0) out.set(uf(e.ufDaOferta), preco);
  }
  return out;
}

// Qual descricao uma oferta carrega (migration 0246) — a do cabecalho da linha E a que o botao
// "Atualizar N produtos" grava no Catalogo quando cria o item.
//
// Precedencia: 1) a DIGITADA no cadastro da oferta, que nenhuma coleta sobrescreve; 2) a da coleta
// corrente, SO se o codigo foi confirmado; 3) o lastro do historico (a consulta ja filtra so os
// confirmados). Coleta com motivo ('codigo_nao_encontrado') guarda o nome do produto que o site
// devolveu NO LUGAR deste codigo — esmalte, inseticida, caixa de som numa linha de cabo.
//
// ⚠️ Peca UNICA porque, ate 12/09/2026, a tela e o botao decidiam cada um a sua: a tela lia a digitada
// e o botao nao, entao a oferta recem-cadastrada (sem coleta ainda) mostrava o nome certo e ia pro
// Catalogo como "Codigo 4204154" — que a cotacao, lendo so o Catalogo (DA-638), exibe. O botao
// tambem nao filtrava o motivo e podia gravar no Catalogo o nome de OUTRO produto.
export interface DescricaoDaOfertaEntrada {
  digitada: string | null | undefined;      // monitor_oferta.descricao
  coletada: string | null | undefined;      // descricao da coleta mais recente
  motivoDaColeta: string | null | undefined; // motivo da coleta mais recente (null = codigo confirmado)
  historico: string | null | undefined;     // ultima descricao de coleta confirmada
}
export function descricaoDaOferta(e: DescricaoDaOfertaEntrada): string | null {
  const util = (s: string | null | undefined) => (s != null && s.trim() !== '' ? s : null);
  return util(e.digitada)
    ?? (e.motivoDaColeta ? null : util(e.coletada))
    ?? util(e.historico);
}

// Grao de arredondamento do preco de venda ao aplicar a media nos produtos (DA-642).
export type GraoArredondamento = 'centavo' | 'dez_centavos';

// Preco de venda arredonda PRA CIMA — arredondar pra baixo entregaria margem menor que o markup,
// e o markup e a decisao. Medido no cadastro: as bitolas 1,5 / 2,5 / 4 / 6 estao todas no centavo
// (custo 1,2997 x 1,30 = 1,68961 -> 1,69, que e o preco praticado), e a de 10mm em 10 centavos.
// ⚠️ O epsilon nao e' folga de negocio, e' o resto binario: MEDIDO varrendo 0,01..2000,00, o ceil
// cru sobe um centavo em precos que JA sao redondos — 0,07 vira 0,08; 1,10 vira 1,11; 2,20 vira
// 2,21 (o primeiro caso e' 0,07 e sao 12 antes de R$ 2,23). Um centavo a mais sem causa visivel,
// que so aparece quando a media cai exatamente num valor cheio. Em 10 centavos nao ocorre nenhum.
export function arredondarPrecoPraCima(v: number, grao: GraoArredondamento): number {
  const passo = grao === 'dez_centavos' ? 10 : 100;
  return Math.ceil(v * passo - 1e-6) / passo;
}

// Variacao (fracao) = (Praticado / Sugestao de Venda) - 1 = (NossoPixUnit / FC Pix) - 1.
// nossoPixUnit = preco do nosso produto JA por unidade. null quando nao da pra comparar
// (sem nosso pix OU sem sugestao de venda).
export function variacaoFrac(fcPixUnitVal: number, nossoPixUnit: number): number | null {
  if (!(fcPixUnitVal > 0) || !(nossoPixUnit > 0)) return null;
  return nossoPixUnit / fcPixUnitVal - 1;
}

// Quantidade da UNIDADE DE VENDA do nosso produto. produtos.preco_pix e o preco
// DESSA unidade inteira, nao por metro/peca — entao nossoPixPorUnidade = preco_pix / qtdUnidade.
// (Ex.: 1308 FLEX 1,5MM, rolo de 100m, preco_pix=156 -> 1,56/m.) Quando o produto e vendido
// por unidade avulsa a qtd e 1. NAO confundir com qtd da embalagem da OFERTA do fornecedor.
export const QTD_UNIDADE_DEFAULT = 100;

// Extrai a quantidade da unidade da descricao do produto (ex.: "FLEX 1,5MM AMA 100MTS" -> 100;
// "...500MTS" -> 500; "...1000M" -> 1000). Aceita MTS / MTR / METROS / M. Fallback null
// (o chamador decide o default — tipicamente 100 — e loga).
export function parseQtdUnidade(descricao: string | null | undefined): number | null {
  if (!descricao) return null;
  const s = String(descricao);
  // 1) forma explicita "<n> MTS/MTR/METROS/M" (com ou sem espaco).
  const m = s.match(/(\d{2,5})\s*M(?:TS|TR|ETROS)?\b/i)
    // 2) forma "x100m" / "X 500 M".
    || s.match(/[x×]\s*(\d{2,5})\s*m\b/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Metragem da embalagem lida da descricao que a MARTINS devolveu na coleta (ex.: "Cabo Flexivel
// Corfio 6mmx100m Rolo 750V Preto" -> 100). Serve pra pegar o codigo REATRIBUIDO: a Martins troca o
// produto por tras do mesmo codigo (o 4200279 era bobina de 500m e virou rolo de 100m) e a metragem
// do cadastro fica pra tras — o custo por metro despenca pra 1/5 do real e a oferta rouba o selo de
// "melhor preco" pra sempre. Deliberadamente CONSERVADORA: so aceita a forma de embalagem "x<N>m"
// (o "x" separador e obrigatorio), pra nunca confundir a BITOLA com a metragem ("2x4mm", "6MM",
// "12mm" nao casam). Sem esse padrao -> null, e o chamador nao mexe em nada.
export function metragemDaDescricaoFornecedor(descricao: string | null | undefined): number | null {
  if (!descricao) return null;
  const m = String(descricao).match(/[x×]\s*(\d{2,5})\s*m\b/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Uma coleta pode APAGAR preco: codigo que tinha valor volta "indisponivel" e o novo estado e o
// que a tela passa a mostrar. Item esgotado de verdade acontece e tem que passar; o que nunca e
// real e a MAIORIA do que tinha preco sumir de uma vez — isso e o robo tendo perdido a sessao do
// fornecedor no meio do lote (02/09/2026: 90 precos enterrados num clique, [DA-660]).
// Conta so o ND MUDO: quando o coletor CONFERIU que o codigo saiu do site, ele manda motivo, e
// esse ND e medicao, nao falha. Limiar duplo de proposito — a fracao sozinha condenaria um lote
// pequeno (1 de 1 = 100%), e o piso sozinho travaria uma faxina legitima num universo grande.
export const COLETA_MIN_PERDAS = 5;
export const COLETA_FRACAO_SUSPEITA = 0.7;
export function loteDerrubaPrecos(tinhamPreco: number, perderiamPreco: number): boolean {
  if (perderiamPreco < COLETA_MIN_PERDAS) return false;
  if (tinhamPreco <= 0) return false;
  return perderiamPreco >= COLETA_FRACAO_SUSPEITA * tinhamPreco;
}

// Nosso Pix por unidade a partir do preco da unidade de venda e da qtd por unidade.
// preco_pix do produto e o preco da unidade inteira; dividir pela qtd -> por metro/peca.
export function nossoPixPorUnidade(precoPixUnidade: number | null, qtdUnidade: number | null): number {
  const preco = precoPixUnidade == null ? 0 : Number(precoPixUnidade);
  const qt = qtdUnidade == null || !(Number(qtdUnidade) > 0) ? QTD_UNIDADE_DEFAULT : Number(qtdUnidade);
  if (!(preco > 0)) return 0;
  return preco / qt;
}
